/**
 * 票链云 — 前后端共用的常量
 *
 * 【为什么要有这个文件】
 * 文件大小上限、允许的类型这些东西，从前是前后端各写一份的，于是必然漂移：
 * 前端 `accept` 收了 `.ofd/.xml`、限制 20MB，后端白名单只有 PDF/图片、限制 10MB。
 * 用户传一个 15MB 的 PDF，前端放行、后端报错，提示还只在服务端日志里。
 *
 * 放到 shared 之后，**两端读的是同一个数**，改一处即两端同时生效。
 */
/**
 * 单文件大小上限（字节）。
 *
 * 取 10MB 的依据：阿里云 OCR 的二进制上传方式上限就是 10MB，
 * 留再多也用不上，反而让前端以为可以传更大的文件。
 */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
/** 供 UI 显示的友好表述 */
export const MAX_UPLOAD_LABEL = '10MB';
/* ==========================================================================
 * 发票上传
 * ========================================================================== */
/**
 * 允许上传的发票文件类型（文档 5.1：支持 PDF、图片）。
 *
 * 【不要往这个集合里加合同用的类型】
 * `invoice.service.ts` 用同一个集合校验发票上传。加 docx 进来等于让
 * **发票接口开始接受 Word 文档**——而 Word 既不可能是发票原件，也过不了 OCR。
 * 合同附件有自己的集合，见下。
 */
export const INVOICE_ALLOWED_MIME_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/bmp',
    'image/tiff',
    'image/webp',
];
/**
 * `<input accept>` 用的扩展名列表（发票原件与发票复印件共用）。
 *
 * 与 `CONTRACT_ACCEPT` 刻意分开：合同可以传 Word，发票不行。
 * 用一个共用的 accept 会让发票页提示"可以传 Word"，传上去却被后端拒绝。
 */
export const INVOICE_ACCEPT = '.pdf,.jpg,.jpeg,.png,.bmp,.tif,.tiff,.webp';
/* ==========================================================================
 * 发票附件的手写内容提取
 * ========================================================================== */
/**
 * 手写提取的超时（毫秒）。
 *
 * 单张图片一次调用，正常在数秒内返回；这个上限是为了兜住超大图与网络异常。
 * 超时后如实标记 TIMEOUT 并保留原件，由用户决定重试还是自行核对——
 * **不静默丢弃，也不因为超时就当成"没有手写内容"**。
 */
export const HANDWRITING_TIMEOUT_MS = 20_000;
/**
 * 手写内容入库的最大字符数，超出显式标记截断。
 *
 * 20_000 的依据：一页票面的手写批注通常在几十字量级，
 * 这个上限已经是"正常人不会超过"的水平；真超了多半是识别把整张纸的
 * 印刷体也当手写吐了出来，截断比存一坨噪声更有用。
 */
export const HANDWRITING_MAX_TEXT_CHARS = 20_000;
/** 最多保留的行数，超出显式标记截断 */
export const HANDWRITING_MAX_LINES = 500;
/* ==========================================================================
 * 合同附件
 * ========================================================================== */
/**
 * 允许上传的合同附件类型。
 *
 * 与发票的白名单**刻意分开**——两者的业务含义完全不同：发票原件必须是
 * 票面影像（PDF/图片），而合同可以是电子文档（PDF/Word）。
 */
export const CONTRACT_ALLOWED_MIME_TYPES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/bmp',
    'image/tiff',
    'image/webp',
];
/** `<input accept>` 用的扩展名列表 */
export const CONTRACT_ACCEPT = '.pdf,.docx,.jpg,.jpeg,.png,.bmp,.tif,.tiff,.webp';
/**
 * 合同附件的解析超时（毫秒）。
 *
 * 本地解析通常在 1 秒内完成；这个上限是为了兜住异常版式（超大 PDF、
 * 字体表损坏导致 pdfjs 卡住）。超时后如实标记 TIMEOUT 并保留原件，
 * 由用户决定重试或手工录入——**不静默丢弃**。
 */
export const CONTRACT_PARSE_TIMEOUT_MS = 20_000;
/** 单份附件最多解析的页数，超出部分显式标记截断 */
export const CONTRACT_MAX_PAGES = 50;
/** 单个表格最多保留的行数，超出显式标记截断 */
export const CONTRACT_MAX_TABLE_ROWS = 500;
/** 正文入库的最大字符数，超出显式标记截断 */
export const CONTRACT_MAX_TEXT_CHARS = 200_000;
/**
 * TEMP 态附件的保留时长（小时）。
 *
 * 「从文件新建合同」时文件先于合同存在，用户可能填到一半就关掉页面。
 * 超过这个时长仍未绑定到合同的附件会被清理任务回收（连同原件）。
 * 24 小时足够覆盖"今天上传明天接着填"的正常节奏。
 */
export const CONTRACT_TEMP_ATTACHMENT_TTL_HOURS = 24;
/**
 * 报销发票允许的上传类型。
 *
 * 与**发票**白名单完全一致（票面影像：PDF/图片），与合同白名单刻意不同——
 * 报销的凭证是发票，不是可编辑的 Word 文档。
 * 复用同一个数组而不是另抄一份：两者一旦分叉，就会出现
 * "发票能传、报销传不了同一张票"这种没人能解释的差异。
 */
export const EXPENSE_ALLOWED_MIME_TYPES = INVOICE_ALLOWED_MIME_TYPES;
/** `<input accept>` 用的扩展名列表（与发票上传一致） */
export const EXPENSE_ACCEPT = INVOICE_ACCEPT;
//# sourceMappingURL=constants.js.map